import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

const errorSchema = z.object({ message: z.string() });

// What can be the *target* of a mention — i.e. the thing being linked to
// from inside another file/project body. Files retain the `doc` kind in
// API payloads for compatibility with the existing storage model.
export const mentionTargetKindValues = ['thread', 'plan', 'doc', 'user'] as const;
export const mentionTargetKindSchema = z.enum(mentionTargetKindValues);

// What kinds of records can *contain* mentions in their body. Today this
// is rich-text-bearing resources only: files/docs and project descriptions.
export const mentionSourceKindValues = ['doc', 'project'] as const;
export const mentionSourceKindSchema = z.enum(mentionSourceKindValues);

export const mentionResultSchema = z.object({
  kind: mentionTargetKindSchema,
  id: z.string().min(1),
  title: z.string().min(1),
  href: z.string().min(1),
});

export const mentionSearchResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('mention'),
  objects: z.array(mentionResultSchema),
});

export const mentionSearchQuerySchema = z.object({
  q: z.string().trim().min(1).max(50),
  kinds: z
    .string()
    .optional()
    .transform((value) => {
      if (!value) return undefined;
      const parts = value
        .split(',')
        .map((kind) => kind.trim())
        .filter((kind) => kind.length > 0);
      return parts.length > 0 ? parts : undefined;
    })
    .pipe(z.array(mentionTargetKindSchema).optional()),
});

// One row per inbound link — "this source mentions this target".
export const backlinkObjectSchema = z.object({
  source_kind: mentionSourceKindSchema,
  source_id: z.string().min(1),
  source_title: z.string().min(1),
  source_href: z.string().min(1),
  target_kind: mentionTargetKindSchema,
  target_id: z.string().min(1),
  created_at: z.string().datetime(),
});

export const backlinksResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('backlink'),
  objects: z.array(backlinkObjectSchema),
});

export const listBacklinksQuerySchema = z.object({
  target_kind: mentionTargetKindSchema,
  target_id: z.string().min(1).max(64),
});

export const mentionsContract = c.router({
  searchMentions: {
    method: 'GET',
    path: '/mentions/search',
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    query: mentionSearchQuerySchema,
    responses: {
      200: mentionSearchResponseSchema,
      401: errorSchema,
      422: errorSchema,
    },
    summary:
      'Search threads, plans, files, and users by title/name for @-mention typeahead. Org-scoped, max 10 results across all kinds.',
  },
  listBacklinks: {
    method: 'GET',
    path: '/mentions/backlinks',
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    query: listBacklinksQuerySchema,
    responses: {
      200: backlinksResponseSchema,
      401: errorSchema,
      403: errorSchema,
    },
    summary:
      'List every doc/project body in the caller’s organization that mentions the given target. Newest-first.',
  },
});

// Convenience alias kept for the editor extension where "kind" reads more
// naturally than "target_kind".
export type MentionKind = z.infer<typeof mentionTargetKindSchema>;
export type MentionSourceKind = z.infer<typeof mentionSourceKindSchema>;
export type MentionResult = z.infer<typeof mentionResultSchema>;
export type MentionSearchResponse = z.infer<typeof mentionSearchResponseSchema>;
export type MentionSearchQuery = z.infer<typeof mentionSearchQuerySchema>;
export type BacklinkObject = z.infer<typeof backlinkObjectSchema>;
export type BacklinksResponse = z.infer<typeof backlinksResponseSchema>;
export type ListBacklinksQuery = z.infer<typeof listBacklinksQuerySchema>;
